import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Monitor, Scan, Printer, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

// Üst bar iletişim bilgileri
const CONTACT_INFO = {
  phone: '+90 532 270 51 74',
  phoneLink: 'tel:+905322705174',
  email: 'info@elcteknoloji.com',
  emailLink: 'mailto:info@elcteknoloji.com',
};

// Zebra ürün kategorileri - sadece TR için
const zebraCategories = [
  {
    id: 'barkod-yazicilar',
    name: 'Barkod Yazıcılar',
    icon: Printer,
    path: '/zebra-urunleri/barkod-yazicilar',
    subcategories: [
      { name: 'Endüstriyel Yazıcılar', path: '/zebra-urunleri/barkod-yazicilar/endustriyel-yazicilar' },
      { name: 'Masaüstü Yazıcılar', path: '/zebra-urunleri/barkod-yazicilar/masaustu-yazicilar' },
      { name: 'Mobil Yazıcılar', path: '/zebra-urunleri/barkod-yazicilar/mobil-yazicilar' },
      { name: 'Kart Yazıcılar', path: '/zebra-urunleri/barkod-yazicilar/kart-yazicilar' },
    ],
  },
  {
    id: 'mobil-bilgisayarlar',
    name: 'Mobil Bilgisayarlar',
    icon: Monitor,
    path: '/zebra-urunleri/mobil-bilgisayarlar',
    subcategories: [
      { name: 'El Terminalleri', path: '/zebra-urunleri/mobil-bilgisayarlar/el-terminalleri' },
      { name: 'Giyilebilir Ürünler', path: '/zebra-urunleri/mobil-bilgisayarlar/giyilebilir-bilgisayarlar' },
      { name: 'Tabletler', path: '/zebra-urunleri/mobil-bilgisayarlar/tabletler' },
      { name: 'Araç Montajlı Bilgisayarlar', path: '/zebra-urunleri/mobil-bilgisayarlar/arac-montajli-bilgisayarlar' },
    ],
  },
  {
    id: 'barkod-okuyucular',
    name: 'Barkod Okuyucular',
    icon: Scan,
    path: '/zebra-urunleri/barkod-okuyucular',
    subcategories: [
      { name: 'Kablolu Barkod Okuyucular', path: '/zebra-urunleri/barkod-okuyucular/kablolu-tarayicilar' },
      { name: 'Kablosuz Barkod Okuyucular', path: '/zebra-urunleri/barkod-okuyucular/kablosuz-tarayicilar' },
      { name: 'Endüstriyel Barkod Okuyucular', path: '/zebra-urunleri/barkod-okuyucular/endustriyel-tarayicilar' },
      { name: 'Kasa Tipi Sabit Okuyucular', path: '/zebra-urunleri/barkod-okuyucular/sabit-montajli-tarayicilar' },
    ],
  },
];

const TurkeyFlag = () => (
  <svg width="24" height="16" viewBox="0 0 360 240" xmlns="http://www.w3.org/2000/svg">
    <rect width="360" height="240" fill="#E30A17"/>
    <circle cx="120" cy="120" r="60" fill="white"/>
    <circle cx="135" cy="120" r="48" fill="#E30A17"/>
    <polygon points="197.6,90.0 203.2,111.1 225.0,112.2 206.7,124.1 212.3,145.2 195.4,131.4 177.1,143.3 184.9,123.0 168.0,109.2 189.8,110.4" fill="white"/>
  </svg>
);

const UKFlag = () => (
  <svg width="24" height="16" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="16" rx="1" fill="#012169"/>
    <path d="M0 0L24 16M24 0L0 16" stroke="white" strokeWidth="2.5"/>
    <path d="M0 0L24 16M24 0L0 16" stroke="#C8102E" strokeWidth="1.5"/>
    <path d="M12 0V16M0 8H24" stroke="white" strokeWidth="4"/>
    <path d="M12 0V16M0 8H24" stroke="#C8102E" strokeWidth="2.5"/>
  </svg>
);

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [isMobileZebraOpen, setIsMobileZebraOpen] = useState(false);
  const megaMenuRef = useRef(null);
  const megaMenuTimeoutRef = useRef(null);
  const location = useLocation();
  const { lang, toggleLang, t } = useLanguage();

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.solutions'), path: '/cozumler' },
    { name: t('nav.professionalServices'), path: '/profesyonel-hizmetler' },
    { name: t('nav.securityProducts'), path: '/guvenlik-urunleri' },
    { name: t('nav.zebraProducts'), path: '/zebra-urunleri', hasMegaMenu: lang === 'tr' },
    { name: t('nav.contact'), path: '/iletisim' },
  ];

  const handleMegaMenuEnter = () => {
    if (megaMenuTimeoutRef.current) {
      clearTimeout(megaMenuTimeoutRef.current);
    }
    setIsMegaMenuOpen(true);
  };

  const handleMegaMenuLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setIsMegaMenuOpen(false);
    }, 150);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      {/* Üst Bar - İletişim Bilgileri */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-end gap-4 md:gap-6 py-2 text-xs md:text-sm">
            <a 
              href={CONTACT_INFO.phoneLink} 
              className="flex items-center gap-2 text-slate-300 hover:text-cyan-400 transition-colors"
            >
              <Phone size={14} />
              <span>{CONTACT_INFO.phone}</span>
            </a>
            <span className="text-slate-600">|</span>
            <a 
              href={CONTACT_INFO.emailLink} 
              className="flex items-center gap-2 text-slate-300 hover:text-cyan-400 transition-colors"
            >
              <Mail size={14} />
              <span>{CONTACT_INFO.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Ana Navbar */}
      <nav
        data-testid="navbar"
        data-agent-nav="main-navigation"
        role="navigation"
        aria-label="Ana Navigasyon"
        className={`fixed top-[36px] md:top-[40px] left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass py-3' : 'bg-slate-950 py-5'
        }`}
      >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
            <div className="flex flex-col">
              <span className="font-outfit font-bold text-xl md:text-2xl">
                <span className="text-cyan-400">elc</span>
                <span className="text-white">teknoloji</span>
              </span>
              <span className="text-[10px] md:text-xs text-slate-400 tracking-wider whitespace-nowrap">OT&IT Cybersecurity Solutions</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              link.hasMegaMenu ? (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={handleMegaMenuEnter}
                  onMouseLeave={handleMegaMenuLeave}
                  ref={megaMenuRef}
                >
                  <Link
                    to={link.path}
                    data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                    className={`flex items-center gap-1 text-xs lg:text-sm font-medium uppercase tracking-wider transition-colors whitespace-nowrap ${
                      location.pathname.startsWith('/zebra-urunleri')
                        ? 'text-cyan-400'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {link.name}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${isMegaMenuOpen ? 'rotate-180' : ''}`} />
                  </Link>

                  {/* Mega Menu Dropdown */}
                  {isMegaMenuOpen && (
                    <div 
                      data-testid="zebra-mega-menu"
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-4 z-50"
                    >
                      <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 rounded-lg shadow-2xl p-6 min-w-[700px]">
                        <div className="grid grid-cols-3 gap-6">
                          {zebraCategories.map((category) => (
                            <div key={category.id} className="space-y-3">
                              <Link
                                to={category.path}
                                className="flex items-center gap-2 text-cyan-400 font-semibold text-sm uppercase tracking-wider hover:text-cyan-300 transition-colors"
                              >
                                <category.icon size={18} />
                                {category.name}
                              </Link>
                              <ul className="space-y-2 pl-6">
                                {category.subcategories.map((sub) => (
                                  <li key={sub.path}>
                                    <Link
                                      to={sub.path}
                                      className="text-slate-400 hover:text-white text-sm transition-colors block py-1"
                                    >
                                      {sub.name}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                        <div className="mt-6 pt-4 border-t border-slate-700/50">
                          <Link
                            to="/zebra-urunleri"
                            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm font-medium transition-colors"
                          >
                            Tüm Zebra Ürünlerini Görüntüle →
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`text-xs lg:text-sm font-medium uppercase tracking-wider transition-colors whitespace-nowrap ${
                    location.pathname === link.path
                      ? 'text-cyan-400'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}

            {/* Language Toggle */}
            <button
              data-testid="lang-toggle"
              onClick={toggleLang}
              aria-label={lang === 'tr' ? 'Switch to English' : "Türkçe'ye geç"}
              className="flex items-center gap-1.5 px-2 py-1.5 min-h-[40px] border border-slate-700 hover:border-cyan-500/50 transition-all rounded-sm"
              title={lang === 'tr' ? 'Switch to English' : 'Türkçe\'ye geç'}
            >
              {lang === 'tr' ? <UKFlag /> : <TurkeyFlag />}
              <span className="text-[10px] font-mono text-slate-400 uppercase">{lang === 'tr' ? 'EN' : 'TR'}</span>
            </button>

            <Link to="/iletisim">
              <Button
                data-testid="nav-cta-button"
                className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-6 py-5 uppercase tracking-widest text-xs btn-glow whitespace-nowrap"
              >
                {t('nav.getQuote')}
              </Button>
            </Link>
          </div>

          {/* Mobile: Lang + Menu */}
          <div className="md:hidden flex items-center gap-3">
            <button
              data-testid="lang-toggle-mobile"
              onClick={toggleLang}
              aria-label={lang === 'tr' ? 'Switch to English' : "Türkçe'ye geç"}
              className="flex items-center gap-1 px-2 py-1.5 min-h-[44px] min-w-[44px] justify-center border border-slate-700 rounded-sm"
            >
              {lang === 'tr' ? <UKFlag /> : <TurkeyFlag />}
              <span className="text-[10px] font-mono text-slate-400 uppercase">{lang === 'tr' ? 'EN' : 'TR'}</span>
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
              aria-expanded={isMobileMenuOpen}
              className="text-white p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden mobile-menu mt-4 pb-6 border-t border-slate-800 bg-slate-950 -mx-6 md:-mx-12 px-6 md:px-12 shadow-2xl"
          >
            <div className="flex flex-col gap-4 pt-4">
              {navLinks.map((link) => (
                link.hasMegaMenu ? (
                  <div key={link.path}>
                    <button
                      data-testid="mobile-zebra-accordion"
                      onClick={() => setIsMobileZebraOpen(!isMobileZebraOpen)}
                      className={`flex items-center justify-between w-full text-sm font-medium uppercase tracking-wider transition-colors text-left ${
                        location.pathname.startsWith('/zebra-urunleri')
                          ? 'text-cyan-400'
                          : 'text-slate-400'
                      }`}
                    >
                      <span>{link.name}</span>
                      <ChevronDown size={16} className={`transition-transform duration-200 ${isMobileZebraOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isMobileZebraOpen && (
                      <div className="mt-3 ml-4 space-y-3 border-l border-slate-700 pl-4">
                        {zebraCategories.map((category) => (
                          <div key={category.id}>
                            <Link
                              to={category.path}
                              className="flex items-center gap-2 text-cyan-400 text-sm font-medium mb-2"
                            >
                              <category.icon size={14} />
                              {category.name}
                            </Link>
                            <div className="space-y-1 ml-5">
                              {category.subcategories.map((sub) => (
                                <Link
                                  key={sub.path}
                                  to={sub.path}
                                  className="block text-slate-400 text-xs py-1"
                                >
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                        <Link
                          to="/zebra-urunleri"
                          className="block text-cyan-400 text-sm mt-2"
                        >
                          Tüm Ürünleri Gör →
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                    className={`text-sm font-medium uppercase tracking-wider transition-colors ${
                      location.pathname === link.path
                        ? 'text-cyan-400'
                        : 'text-slate-400'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <Link to="/iletisim" className="mt-2">
                <Button
                  data-testid="mobile-nav-cta-button"
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-none px-6 py-5 uppercase tracking-widest text-xs"
                >
                  {t('nav.getQuote')}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navbar;
